import React, { useContext, useEffect, useState } from 'react';
import { CalendarDays, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const PERIODS = [
  ['day', 'Today', "Today's figures so far"],
  ['week', 'This week', 'Since the last weekly send'],
  ['month', 'Last month', 'Whole previous calendar month'],
  ['custom', 'Pick dates', 'Any start and end date'],
];

const summarise = (d) => {
  const entries = [...Object.entries(d.sms || {}), ...Object.entries(d.email || {})];
  const fails = entries.filter(([, v]) => String(v).startsWith('failed'));
  return { sent: entries.length - fails.length, fails };
};

// One row per person: profit-access users first, then any fixed numbers/emails from settings not already covered.
const buildRecipients = (cfg) => {
  const rows = (cfg.profit_access_recipients || []).map((u) => ({ id: u.email || u.phone, name: u.name, phone: u.phone || null, email: u.email || null }));
  const phones = new Set(rows.map((r) => r.phone).filter(Boolean));
  const emails = new Set(rows.map((r) => (r.email || '').toLowerCase()).filter(Boolean));
  (cfg.sms_numbers || []).forEach((p) => { if (!phones.has(p)) { rows.push({ id: `sms:${p}`, name: 'Extra number', phone: p, email: null }); phones.add(p); } });
  (cfg.emails || []).forEach((e) => { if (!emails.has(e.toLowerCase())) { rows.push({ id: `email:${e}`, name: 'Extra email', phone: null, email: e }); emails.add(e.toLowerCase()); } });
  return rows;
};

export function SendProfitNowButton({ onSent }) {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('day');
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [ticked, setTicked] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    if (period === 'custom' && (!startDate || !endDate)) { setPreview(null); return undefined; }
    setPreview(null);
    const ctrl = new AbortController();
    const qs = period === 'custom' ? `period=custom&start_date=${startDate}&end_date=${endDate}` : `period=${period}`;
    fetch(`${API_URL}/api/staff/profit-centre/weekly-digest/preview?${qs}`, { credentials: 'include', signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setPreview(d)).catch(() => {});
    return () => ctrl.abort();
  }, [open, period, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API_URL}/api/staff/profit-centre/weekly-digest`, { credentials: 'include' })
      .then((r) => r.json())
      .then((cfg) => { const rows = buildRecipients(cfg); setRecipients(rows); setTicked(Object.fromEntries(rows.map((r) => [r.id, true]))); })
      .catch(() => toast.error('Could not load recipients'))
      .finally(() => setLoading(false));
  }, [open]);

  if (user?.role !== 'admin') return null;

  const selected = recipients.filter((r) => ticked[r.id]);
  const allOn = recipients.length > 0 && selected.length === recipients.length;
  const setAll = (v) => setTicked(Object.fromEntries(recipients.map((r) => [r.id, v])));
  const onlyMe = () => setTicked(Object.fromEntries(recipients.map((r) => [r.id, (r.email || '').toLowerCase() === (user?.email || '').toLowerCase()])));

  const send = async () => {
    setBusy(true);
    try {
      const body = {
        period,
        ...(period === 'custom' ? { start_date: startDate, end_date: endDate } : {}),
        sms_numbers: [...new Set(selected.map((r) => r.phone).filter(Boolean))],
        emails: [...new Set(selected.map((r) => r.email).filter(Boolean))],
      };
      const r = await fetch(`${API_URL}/api/staff/profit-centre/weekly-digest/send-now`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      const { sent, fails } = summarise(d);
      const profit = d.total ? `Profit £${Math.round(d.total.profit).toLocaleString('en-GB')}` : '';
      const who = `${selected.length} ${selected.length === 1 ? 'person' : 'people'} · ${body.sms_numbers.length} text${body.sms_numbers.length === 1 ? '' : 's'} + ${body.emails.length} email${body.emails.length === 1 ? '' : 's'}`;
      if (fails.length) toast.warning(`${profit} — ${sent} sent, ${fails.length} failed`, { description: fails.map(([k, v]) => `${k}: ${v}`).join(' · ').slice(0, 300) });
      else toast.success(`${profit} — sent to ${who}`, { description: selected.map((s) => s.name).join(', ') });
      setOpen(false);
      onSent?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-[#3b2508] font-bold shrink-0" data-testid="pc-send-profit-now">
        <Send className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Send profit text now</span>
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-md" data-testid="pc-send-profit-dialog">
          <DialogHeader>
            <DialogTitle>Send profit text + email now</DialogTitle>
            <DialogDescription>ProBound, ProEpoxy, ProDecor and total — ex VAT (Training is in the monthly). Untick anyone you don't want to send to this time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid="pc-send-profit-period">
              {PERIODS.map(([k, l, sub]) => (
                <button key={k} type="button" role="radio" aria-checked={period === k} onClick={() => setPeriod(k)} data-testid={`pc-send-profit-period-${k}`}
                  className={`rounded-lg border p-2 text-left ${period === k ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <div className="text-sm font-bold flex items-center gap-1">{k === 'custom' && <CalendarDays className="w-3.5 h-3.5" />}{l}</div><div className="text-[11px] text-gray-500">{sub}</div>
                </button>
              ))}
            </div>
            {period === 'custom' && (
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-2" data-testid="pc-send-profit-dates">
                <label className="text-[11px] font-semibold text-gray-600">From<Input type="date" value={startDate} max={todayIso()} onChange={(e) => setStartDate(e.target.value)} className="h-9 mt-0.5 bg-white" data-testid="pc-send-profit-start" /></label>
                <label className="text-[11px] font-semibold text-gray-600">To<Input type="date" value={endDate} max={todayIso()} onChange={(e) => setEndDate(e.target.value)} className="h-9 mt-0.5 bg-white" data-testid="pc-send-profit-end" /></label>
              </div>
            )}
            <details className="rounded-lg border bg-gray-50 text-xs" data-testid="pc-send-profit-preview">
              <summary className="px-3 py-2 cursor-pointer font-semibold text-gray-700 flex items-center justify-between">
                <span>Preview of the text</span>
                {preview ? <span className="font-bold text-emerald-700" data-testid="pc-send-profit-preview-total">Profit £{Math.round(preview.report.total.profit).toLocaleString('en-GB')}</span> : <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
              </summary>
              <pre className="px-3 pb-3 whitespace-pre-wrap font-mono text-gray-700 max-h-48 overflow-y-auto" data-testid="pc-send-profit-preview-sms">{preview?.sms || 'Loading…'}</pre>
            </details>
            <div className="rounded-lg border" data-testid="pc-send-profit-recipients">
              <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 text-xs">
                <span className="font-semibold">Recipients ({selected.length}/{recipients.length})</span>
                <span className="flex gap-3">
                  <button type="button" className="underline" onClick={() => setAll(!allOn)} data-testid="pc-send-profit-toggle-all">{allOn ? 'Untick all' : 'Tick all'}</button>
                  <button type="button" className="underline" onClick={onlyMe} data-testid="pc-send-profit-only-me">Just me</button>
                </span>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y">
                {loading && <div className="p-3 text-xs text-gray-500 inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
                {!loading && recipients.length === 0 && <div className="p-3 text-xs text-gray-500">No recipients yet — add them in the Weekly digest tab.</div>}
                {recipients.map((r) => (
                  <label key={r.id} className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" data-testid={`pc-send-profit-recipient-${r.id}`}>
                    <Checkbox checked={!!ticked[r.id]} onCheckedChange={(v) => setTicked((t) => ({ ...t, [r.id]: !!v }))} className="mt-0.5" data-testid={`pc-send-profit-check-${r.id}`} />
                    <span className="min-w-0 text-sm">
                      <span className="block font-semibold truncate">{r.name}</span>
                      <span className="block text-[11px] text-gray-500 truncate">{r.phone || <span className="text-amber-700">no mobile</span>} · {r.email || <span className="text-amber-700">no email</span>}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={send} disabled={busy || loading || selected.length === 0 || (period === 'custom' && (!startDate || !endDate))} className="w-full bg-[#3b2508] hover:bg-[#523310] text-white font-bold" data-testid="pc-send-profit-send">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send now{selected.length ? ` to ${selected.length}` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
